import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import PermissionGuard from './components/PermissionGuard';
import Login from './components/Login';
import Layout from './components/Layout';
import Inventory from './components/Inventory';
import Orders from './components/Orders';
import ExtOrders from './components/ExtOrders';
import OrderDetail from './components/OrderDetail';
import Catalog from './components/Catalog';
import Accounts from './components/Accounts';
import Users from './components/Users';
import PendingApprovals from './components/PendingApprovals';
import WarehouseReceipts from './components/WarehouseReceipts';
import BOLs from './components/BOLs';
import RateCalculator from './components/RateCalculator';
import Invoices from './components/Invoices';
import Profile from './components/Profile';
import './App.css';

// Protected Route component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return null; // or a loading spinner
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  return (
    <Router>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <TenantProvider>
        <AuthProvider>
          <Routes>
            {/* Public route */}
            <Route path="/login" element={<Login />} />
            
            {/* Tenant-specific public route */}
            <Route path="/:tenantPrefix/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/bols" replace />} />
              <Route path="bols" element={
                <PermissionGuard module="transportation">
                  <BOLs />
                </PermissionGuard>
              } />
              <Route path="rate-calculator" element={
                <PermissionGuard module="transportation">
                  <RateCalculator />
                </PermissionGuard>
              } />
              <Route path="inventory" element={
                <PermissionGuard module="inventory">
                  <Inventory />
                </PermissionGuard>
              } />
              <Route path="orders" element={
                <PermissionGuard module="inventory">
                  <Orders />
                </PermissionGuard>
              } />
              <Route path="ext-orders" element={
                <PermissionGuard module="ext_orders">
                  <ExtOrders />
                </PermissionGuard>
              } />
              <Route path="orders/:orderId" element={
                <PermissionGuard module="inventory">
                  <OrderDetail />
                </PermissionGuard>
              } />
              <Route path="warehouse-receipts" element={
                <PermissionGuard module="warehouse_receipts">
                  <WarehouseReceipts />
                </PermissionGuard>
              } />
              <Route path="invoices" element={
                <PermissionGuard module="invoices">
                  <Invoices />
                </PermissionGuard>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="catalog" element={
                <PermissionGuard module="inventory">
                  <Catalog />
                </PermissionGuard>
              } />
              <Route path="accounts" element={
                <PermissionGuard module="accounts">
                  <Accounts />
                </PermissionGuard>
              } />
              <Route path="users" element={
                <PermissionGuard module="admin">
                  <Users />
                </PermissionGuard>
              } />
              <Route path="pending-approvals" element={
                <PermissionGuard module="inventory" permission="approve">
                  <PendingApprovals />
                </PermissionGuard>
              } />
              <Route path="*" element={<Navigate to="/bols" replace />} />
            </Route>
            
            {/* Tenant-specific protected routes */}
            <Route path="/:tenantPrefix" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="bols" replace />} />
              <Route path="bols" element={<BOLs />} />
              <Route path="rate-calculator" element={<RateCalculator />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="orders" element={<Orders />} />
              <Route path="ext-orders" element={<ExtOrders />} />
              <Route path="orders/:orderId" element={<OrderDetail />} />
              <Route path="warehouse-receipts" element={<WarehouseReceipts />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="profile" element={<Profile />} />
              <Route path="catalog" element={<Catalog />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="users" element={<Users />} />
              <Route path="pending-approvals" element={<PendingApprovals />} />
            </Route>
          </Routes>
        </AuthProvider>
      </TenantProvider>
    </Router>
  );
}

export default App;
